import { ref } from 'vue';
import _ from 'lodash';

import { fetchTaxiVendors } from '@/services/partners/taxiVendors/fetchTaxiVendors';
import { PartnerCodeName } from '@/models/partner';
import type { ListType } from '@/models/app';
import { RideType, RideTypeKey } from '@/models/rideType';
import { partnerCodesForUberAssist } from '@/constants/partner';

const specialOrderRideTypes = [RideType.UberAssist];

const partnerOrderRideTypesMap = new Map([
  [PartnerCodeName.TOLLESON, [RideType.UberX, RideType.Espanol, RideType.WAV]],
  [PartnerCodeName.INNISFIL, [...RideType.ALL, RideType.InnisfilTransit]],
  // other partners will be [...RideType.ALL]
]);

const partnerScheduledRegularRideTypesMap = new Map([
  [PartnerCodeName.TOLLESON, [RideType.UberX, RideType.Espanol, RideType.WAV, RideType.UberAssist]],
  [PartnerCodeName.INNISFIL, [...RideType.ALL, RideType.InnisfilTransit]],
  [PartnerCodeName.STA, [...RideType.ALL, RideType.Stawav]],
  [PartnerCodeName.STA20, [...RideType.ALL, RideType.Stawav]],
  [PartnerCodeName.STA5, [...RideType.ALL, RideType.Stawav]],
  [PartnerCodeName.STA100, [...RideType.ALL, RideType.Stawav]],
  [PartnerCodeName.STAVET20, [...RideType.ALL, RideType.Stawav]],
  [PartnerCodeName.STAVET40, [...RideType.ALL, RideType.Stawav]],
  [PartnerCodeName.MASONIC, [...RideType.ALL, RideType.Stawav]],
  // other partners will be [...RideType.ALL]
]);

// global state
const localTaxis = ref<RideType[]>([]);
const partnerScheduledRideTypesMap = ref(new Map<PartnerCodeName, RideType[]>());

export const useRideTypes = () => {
  const fetchLocalTaxis = async () => {
    const res = await fetchTaxiVendors();

    const newLocalTaxis: RideType[] = [];

    const newPartnerTaxiRideTypesMap = new Map([...partnerScheduledRegularRideTypesMap.entries()].map(([key, value]) => [key, [...value]]));

    for (const taxiVendor of res) {
      for (const partner of taxiVendor.partners || []) {
        const partnerCodeName = partner.codeName as PartnerCodeName;

        const partnerTaxiVendor = taxiVendor.partnerTaxiVendors?.find((p) => p.partnerId === partner.id);

        const rideType = new RideType(taxiVendor.codeName!, 'other' as any, partnerCodeName.toUpperCase() as any, taxiVendor.name!, {
          partnerTaxiVendor: {
            applySubsidyCondition: partnerTaxiVendor?.applySubsidyCondition ?? false,
          },
        });

        newLocalTaxis.push(rideType);

        if (!newPartnerTaxiRideTypesMap.has(partnerCodeName)) {
          newPartnerTaxiRideTypesMap.set(partnerCodeName, [...RideType.ALL]);
        }
        newPartnerTaxiRideTypesMap.get(partnerCodeName)!.push(rideType);
      }
    }

    localTaxis.value = newLocalTaxis;
    partnerScheduledRideTypesMap.value = newPartnerTaxiRideTypesMap;
  };

  const combinePartnerRideTypeMaps = (...maps: Map<PartnerCodeName, RideType[]>[]) => {
    const result = new Map<PartnerCodeName, RideType[]>();

    for (const map of maps) {
      for (const [partnerCode, rideTypes] of map.entries()) {
        const mergedList = [...(result.get(partnerCode) ?? []), ...rideTypes];
        result.set(partnerCode, Array.from(new Set(mergedList)));
      }
    }

    return result;
  };

  const getOrderRideTypes = (partnerCode?: string, preferredVendorRideTypes?: string[]): ListType[] => {
    return getRideTypes({
      mapping: partnerOrderRideTypesMap,
      partnerCode,
      preferredVendorRideTypes,
      specialRideTypes: specialOrderRideTypes,
    });
  };

  const getScheduledRideTypes = (partnerCode?: string, preferredVendorRideTypes?: string[]): ListType[] => {
    return getRideTypes({
      mapping: partnerScheduledRideTypesMap.value,
      partnerCode,
      preferredVendorRideTypes,
    });
  };

  const getAllAvailableRideTypes = (partnerCode?: string): ListType[] => {
    const combinedMaps = combinePartnerRideTypeMaps(partnerOrderRideTypesMap, partnerScheduledRideTypesMap.value);
    return getRideTypes({
      mapping: combinedMaps,
      partnerCode,
      specialRideTypes: specialOrderRideTypes,
    });
  };

  const getRideTypes = ({
    mapping,
    partnerCode,
    preferredVendorRideTypes,
    specialRideTypes,
  }: {
    mapping: Map<PartnerCodeName, RideType[]>;
    partnerCode?: string;
    preferredVendorRideTypes?: string[];
    specialRideTypes?: RideType[];
  }): ListType[] => {
    let rideTypes = RideType.ALL;

    if (partnerCode && mapping.has(partnerCode as PartnerCodeName)) {
      rideTypes = mapping.get(partnerCode as PartnerCodeName)!;
    }

    rideTypes = [...rideTypes, ...(specialRideTypes ?? [])];

    const rideTypeList = rideTypes.map((type, idx) => {
      const label =
        type.key === RideTypeKey.car && preferredVendorRideTypes?.length
          ? `Car (Auto selects a vendor based on partner preference - ${preferredVendorRideTypes.join(', ')})`
          : type.description;

      return {
        id: idx + 1,
        key: type.key,
        label,
      };
    });

    return _.uniqBy(rideTypeList, 'key');
  };

  const findLocalTaxiRideType = (codeName: string): RideType | undefined => {
    return localTaxis.value.find((item) => item.key === codeName);
  };

  const findPartnerLocalTaxiRideType = (partnerCodeName: string, codeName: string): RideType | undefined => {
    return localTaxis.value.find((item) => item.key === codeName && item.vendor === partnerCodeName.toUpperCase());
  };

  const getDefaultRideType = (customerInfo?: { isElbowAssist?: boolean; rideType?: string }, partnerCode?: string): string => {
    if (customerInfo?.rideType) {
      return customerInfo.rideType;
    }

    const requiresElbowAssistance = customerInfo?.isElbowAssist === true;
    const partnerHasUberAssistDefault = partnerCode ? partnerCodesForUberAssist.includes(partnerCode) : false;

    const shouldUseUberAssist = requiresElbowAssistance || partnerHasUberAssistDefault;
    return shouldUseUberAssist ? RideType.UberAssist.key : '';
  };

  const getAvailableRideType = ({
    rideType,
    partnerCode,
    rideTypes,
    customerInfo,
  }: {
    rideType?: string;
    partnerCode?: string;
    rideTypes: ListType[];
    customerInfo?: {
      isElbowAssist?: boolean;
      rideType?: string;
    };
  }) => {
    // We use some special ride types on each page and need to validate rideType is available on the page
    const defaultRideType = getDefaultRideType(customerInfo, partnerCode);
    const isAvailableDefaultRideType = rideTypes.some((item) => item.key === defaultRideType);
    if (isAvailableDefaultRideType) {
      return defaultRideType;
    }

    const isAvailable = rideTypes.some((item) => item.key === rideType);
    if (isAvailable) {
      return rideType;
    }

    return rideTypes[0].key;
  };

  return {
    fetchLocalTaxis,
    getOrderRideTypes,
    getScheduledRideTypes,
    findLocalTaxiRideType,
    findPartnerLocalTaxiRideType,
    getDefaultRideType,
    getAllAvailableRideTypes,
    getAvailableRideType,
  } as const;
};
